import type { PlatformAdapter, ToastParams, PopupParams, ShareData, GeoPosition } from "@cogo/runtime"

/**
 * WebPlatformAdapter — browser implementation of PlatformAdapter.
 * Wraps all browser APIs (window, document, navigator, localStorage).
 */
export class WebPlatformAdapter implements PlatformAdapter {
  private toastHandler: ((p: ToastParams) => void) | null = null
  private popupHandler: ((p: PopupParams) => void) | null = null
  private loadingHandler: ((show: boolean) => void) | null = null

  setToastHandler(handler: (p: ToastParams) => void): void {
    this.toastHandler = handler
  }

  setPopupHandler(handler: (p: PopupParams) => void): void {
    this.popupHandler = handler
  }

  setLoadingHandler(handler: (show: boolean) => void): void {
    this.loadingHandler = handler
  }

  // -- Navigation --
  pushRoute(path: string): void {
    if (typeof window === "undefined") return
    window.history.pushState({}, "", path)
  }

  replaceRoute(path: string): void {
    if (typeof window === "undefined") return
    window.history.replaceState({}, "", path)
  }

  goBack(): void {
    if (typeof window === "undefined") return
    window.history.back()
  }

  openExternal(url: string, newTab = true): void {
    if (typeof window === "undefined") return
    if (newTab) {
      window.open(url, "_blank", "noopener,noreferrer")
    } else {
      window.location.href = url
    }
  }

  getCurrentPath(): string {
    if (typeof window === "undefined") return "/"
    return window.location.pathname + window.location.search
  }

  // -- Storage --
  getItem(key: string): string | null {
    if (typeof localStorage === "undefined") return null
    return localStorage.getItem(key)
  }

  setItem(key: string, value: string): void {
    if (typeof localStorage === "undefined") return
    localStorage.setItem(key, value)
  }

  removeItem(key: string): void {
    if (typeof localStorage === "undefined") return
    localStorage.removeItem(key)
  }

  clearStorage(): void {
    if (typeof localStorage === "undefined") return
    localStorage.clear()
  }

  // -- Clipboard --
  async copyToClipboard(text: string): Promise<void> {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      await navigator.clipboard.writeText(text)
      return
    }
    // Fallback for older browsers
    if (typeof document === "undefined") return
    const textarea = document.createElement("textarea")
    textarea.value = text
    textarea.style.position = "fixed"
    textarea.style.opacity = "0"
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand("copy")
    document.body.removeChild(textarea)
  }

  async readClipboard(): Promise<string> {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      return navigator.clipboard.readText()
    }
    return ""
  }

  // -- UI Feedback --
  showToast(params: ToastParams): void {
    this.toastHandler?.(params)
  }

  showPopup(params: PopupParams): void {
    this.popupHandler?.(params)
  }

  showLoading(show: boolean): void {
    this.loadingHandler?.(show)
  }

  // -- Scroll --
  scrollToElement(id: string, smooth = true): void {
    if (typeof document === "undefined") return
    const el = document.getElementById(id) ?? document.querySelector(`[data-id="${id}"]`)
    el?.scrollIntoView({ behavior: smooth ? "smooth" : "auto" })
  }

  scrollToPosition(y: number, smooth = true): void {
    if (typeof window === "undefined") return
    window.scrollTo({ top: y, behavior: smooth ? "smooth" : "auto" })
  }

  getScrollY(): number {
    if (typeof window === "undefined") return 0
    return window.scrollY
  }

  // -- System --
  print(): void {
    if (typeof window === "undefined") return
    window.print()
  }

  async share(data: ShareData): Promise<void> {
    if (typeof navigator !== "undefined" && navigator.share) {
      await navigator.share(data)
    }
  }

  async getGeolocation(): Promise<GeoPosition> {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      return { latitude: 0, longitude: 0 }
    }
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
        (err) => reject(new Error(err.message)),
      )
    })
  }

  async getUserMedia(constraints: { audio?: boolean; video?: boolean }): Promise<unknown> {
    if (typeof navigator === "undefined" || !navigator.mediaDevices) return null
    return navigator.mediaDevices.getUserMedia(constraints)
  }

  download(url: string, filename: string): void {
    if (typeof document === "undefined") return
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    a.style.display = "none"
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  async pickFile(accept?: string): Promise<{ name: string; data: unknown } | null> {
    if (typeof document === "undefined") return null
    return new Promise((resolve) => {
      const input = document.createElement("input")
      input.type = "file"
      if (accept) input.accept = accept
      input.onchange = () => {
        const file = input.files?.[0]
        if (file) {
          resolve({ name: file.name, data: file })
        } else {
          resolve(null)
        }
        document.body.removeChild(input)
      }
      input.style.display = "none"
      document.body.appendChild(input)
      input.click()
    })
  }

  showNotification(title: string, body?: string): void {
    if (typeof Notification === "undefined") return
    if (Notification.permission === "granted") {
      new Notification(title, { body })
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then((perm) => {
        if (perm === "granted") new Notification(title, { body })
      })
    }
  }

  async digest(algorithm: string, data: string): Promise<string> {
    if (typeof globalThis.crypto?.subtle === "undefined") return ""
    const encoder = new TextEncoder()
    const buf = await globalThis.crypto.subtle.digest(algorithm, encoder.encode(data))
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("")
  }

  async fetch(url: string, options?: RequestInit): Promise<Response> {
    return globalThis.fetch(url, options)
  }

  createEventSource(url: string, onMessage: (data: string) => void): { close(): void } {
    if (typeof EventSource === "undefined") return { close() {} }
    const es = new EventSource(url)
    es.onmessage = (event) => onMessage(event.data)
    return { close: () => es.close() }
  }

  createWebSocket(url: string) {
    const ws = new WebSocket(url)
    return {
      send(data: string) { ws.send(data) },
      close() { ws.close() },
      onMessage(handler: (data: unknown) => void) { ws.onmessage = (e) => handler(e.data) },
      onClose(handler: () => void) { ws.onclose = handler },
    }
  }

  dispatchAnalyticsEvent(event: string, data?: Record<string, unknown>): void {
    if (typeof CustomEvent !== "undefined") {
      globalThis.dispatchEvent(new CustomEvent("cogo:analytics", {
        detail: { event, ...data },
      }))
    }
  }
}
